import { GroupLevel } from "../types/cpEnums";
import { LocationLevel } from "../graphql/__generated__/graphql";
import { worldGroupDatum } from "../graphql/queries/getLocationsMetadata";

export { LocationLevel };

export enum LocationStringPrefixes {
  Country = "country",
  Group = "group",
}

export const getNumericIdFromStringLocationId = (
  input: string | undefined,
): number | undefined => {
  if (input) {
    let strippedStringValue = input
      .replace(`${LocationStringPrefixes.Country}-`, "")
      .replace(`${LocationStringPrefixes.Group}-`, "");
    return Number(strippedStringValue);
  } else {
    return undefined;
  }
};

export const getLocationLevelFromStringLocationId = (
  input: string | undefined,
): LocationLevel | undefined => {
  let locationLevel: LocationLevel | undefined = undefined;
  if (input) {
    if (input.includes(LocationStringPrefixes.Country)) {
      locationLevel = LocationLevel.Country;
    } else if (input.includes(LocationStringPrefixes.Group)) {
      locationLevel = LocationLevel.Group;
    }
  }
  return locationLevel;
};

export const getGroupLevelFromGroupMetadatum = (input: { groupType?: string; [key: string]: unknown } | undefined) => {
  let groupLevel: GroupLevel | undefined;
  if (input === undefined) {
    groupLevel = undefined;
  } else {
    let { groupType } = input;
    if (groupType === GroupLevel.Region) {
      groupLevel = GroupLevel.Region;
    } else if (groupType === GroupLevel.Subregion) {
      groupLevel = GroupLevel.Subregion;
    } else {
      groupLevel = undefined;
    }
  }

  return groupLevel;
};

/* A utility function to convert product String IDs, e.g., `product-hs92-1`, into numbers, e.g., 1,
for use in GraphQL queries */
export const convertProductStringToNumberId = (
  input: string | undefined,
): number | undefined => {
  if (input) {
    return +input
      .replace("product-HS92-", "")
      .replace("product-SITC-", "")
      .replace("product-HS12-", "")
      .replace("product-HS22-", "");
  } else {
    return undefined;
  }
};

export const capitalizeFirstLetter = (str: string) => {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
};

/**
 * Resolve a location metadatum to a display-ready name.
 *
 * Handles:
 *  - Countries: applies "the " prefix when `thePrefix` is truthy, returns `nameShortEn`
 *  - Groups (regions/subregions): returns `groupName`
 *  - World override: prepends "the " to the world group name
 *
 * Returns `undefined` when the metadatum is missing.
 */
export const getLocationDisplayName = (
  metadatum: Record<string, unknown> | undefined,
  locationLevel: LocationLevel | undefined,
): string | undefined => {
  if (!metadatum) return undefined;

  if (locationLevel === LocationLevel.Country) {
    const prefix = metadatum.thePrefix ? "the " : "";
    return prefix + (metadatum.nameShortEn as string);
  }

  if (locationLevel === LocationLevel.Group) {
    return metadatum.groupName as string;
  }

  return undefined;
};

// ── Default country pool for random initial selection ────────────

export const defaultCountryPool = [
  "country-840", // US
  "country-826", // UK
  "country-392", // Japan
  "country-156", // China
  "country-702", // Singapore
  "country-124", // Canada
  "country-484", // Mexico
  "country-170", // Colombia
  "country-250", // France
  "country-643", // Russia
  "country-356", // India
  "country-76",  // Brazil
  "country-792", // Turkey
  "country-276", // Germany
  "country-704", // Vietnam
  "country-288", // Ghana
  "country-36",  // Australia
  "country-724", // Spain
  "country-400", // Jordan
  "country-682", // Saudi Arabia
];

export const generateRandomCountrySelection = () => {
  return defaultCountryPool[Math.floor(Math.random() * defaultCountryPool.length)];
};

export const otherGroupId: string = "group-998";
export const undeclaredCountryId: string = "country-999";
export const unidentifiedServicesCountryId: string = "country-914";
export const servicesPartnersGroupId: string = "group-994";

// ── Location qualifier utilities ──────────────────────────────────

export const getLocationQualifiers = ({ exporter, importer }: { exporter: string | undefined; importer: string | undefined }) => {
  let exporterLocationLevel = getLocationLevelFromStringLocationId(exporter);
  let importerLocationLevel = getLocationLevelFromStringLocationId(importer);
  let exporterIsUndefined = !exporter;
  let exporterIsCountry = exporterLocationLevel === LocationLevel.Country;
  let exporterIsGroup = exporterLocationLevel === LocationLevel.Group;
  let exporterIsWorld = exporter === worldGroupDatum.groupId;
  let importerIsUndefined = !importer;
  let importerIsCountry = importerLocationLevel === LocationLevel.Country;
  let importerIsGroup = importerLocationLevel === LocationLevel.Group;
  let importerIsWorld = importer === worldGroupDatum.groupId;

  return {
    exporterLocationLevel,
    importerLocationLevel,
    exporterIsUndefined,
    exporterIsCountry,
    exporterIsGroup,
    exporterIsWorld,
    importerIsUndefined,
    importerIsCountry,
    importerIsGroup,
    importerIsWorld,
  };
};

export const sortByLocationName = (
  a: { nameEn?: string; groupName?: string },
  b: { nameEn?: string; groupName?: string },
) => {
  const nameA = (a.nameEn || a.groupName || "").toUpperCase();
  const nameB = (b.nameEn || b.groupName || "").toUpperCase();
  return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
};

export const sortByProductCode = (a: { productCode?: string }, b: { productCode?: string }) => {
  const productCodeA = a.productCode;
  const productCodeB = b.productCode;
  if (productCodeA < productCodeB) {
    return -1;
  }
  if (productCodeA > productCodeB) {
    return 1;
  }
  return 0;
};

export const determineAPICallLocationOrder = ({ variables }: { variables: { countryId?: number; partnerCountryId?: number; groupId?: number; partnerGroupId?: number } | null | undefined }) => {
  if (!variables) {
    return { locationForAPI: undefined, partnerForAPI: undefined };
  }

  let { countryId, partnerCountryId, groupId, partnerGroupId } = variables;
  let locationForAPI: string | undefined = undefined;
  let partnerForAPI: string | undefined = undefined;

  if (countryId !== undefined) {
    locationForAPI = `${LocationStringPrefixes.Country}-${countryId}`;
  } else if (groupId !== undefined) {
    locationForAPI = `${LocationStringPrefixes.Group}-${groupId}`;
  }

  if (partnerCountryId !== undefined) {
    partnerForAPI = `${LocationStringPrefixes.Country}-${partnerCountryId}`;
  } else if (partnerGroupId !== undefined) {
    partnerForAPI = `${LocationStringPrefixes.Group}-${partnerGroupId}`;
  }

  return { locationForAPI, partnerForAPI };
};
